// IEA Site Survey Map screen. Pure: state in, HTML out.
// Mockup: MOCKUP_IEA_NODE_MAP.html v7 (approved 2026-10-15). Words: IEA_STEP3_MAP_WORDS.md.
// Actions use data-mp so they never collide with data-sv (shell) or data-act (admin pages).

import { assetHref } from "./asset-page";
import { esc } from "./training-screens";
import type { MapWarnLine, PackAsset } from "./survey";
import {
  countBelow,
  fullId,
  isTransfer,
  nodeLabel,
  nodeSub,
  pathTo,
  placeOf,
  roomOfNode,
  roomWords,
  shortName,
  type Gaps,
  type MapLink,
  type MapNode,
  type MapTree,
  type Registry,
} from "./survey-map";

const ROOM_TINTS = ["#e3ecff", "#e2f4e7", "#fcecd9", "#efe4fa", "#dff3f4", "#fde2e4", "#e9f0d6", "#e6e6f7"];

/** A steady color per room, in the site's room order. */
export function roomTint(rooms: readonly string[], room: string | null): string {
  if (!room) return "#ffffff";
  const i = rooms.indexOf(room);
  return ROOM_TINTS[(i < 0 ? 0 : i) % ROOM_TINTS.length]!;
}

export interface MapView {
  siteNumber: string;
  siteName: string;
  reg: Registry;
  links: MapLink[];
  tree: MapTree;
  cur: string | null;
  focus: boolean;
  fold: ReadonlySet<string>;
  recent: string[];
  scale: number;
  gaps: Gaps;
  online: boolean;
  waiting: number;
  large: boolean;
  /** The Chain panel's size on this iPad (Robert 2026-10-16). 1 is normal. */
  chainScale?: number;
  /** Collect mode (pass 2): rings, wire tags, "N left" pills, the card panel. Absent in Map. */
  collect?: CollectDecor;
  /** Photos mode: a camera and the photo count on each node, the photo panel. Absent in Map and Collect. */
  photos?: PhotoDecor;
  /** Admins see the Photos button (storage rule 3: admins read the photos). */
  photosOk?: boolean;
  /** In Collect: the room cards (SURVEY_CARDS_C1_WORDS.md) or the diagram. */
  collectView?: "cards" | "diagram" | "oneline";
  /** Gear View step 4: a housing with a door draws as one node. Its count and word ("41 buckets"), or null. */
  gear?: (assetId: string) => { count: number; word: string } | null;
  /** Loads On: every load hangs in a stacked column beside its housing (Robert's LOTO picture). */
  loadsOn?: boolean;
  /** The one amber warning line under the header (IEA_SURVEY_ENTRY_WORDS.md section 4). Null or absent: no line. */
  warn?: MapWarnLine | null;
  /** The site pack is downloading. The line's Refresh Site Pack says so. */
  packBusy?: boolean;
}

/** What Photos mode lays over the diagram (survey-collect.ts counts, survey-photos-screens.ts draws the panel). */
export interface PhotoDecor {
  /** Photos taken for the node, or null for none shown (rooms and loads). */
  count: (key: string) => number | null;
  total: number;
  panel: string;
}

export type MapMode = "map" | "collect" | "photos";

const modeOf = (v: Pick<MapView, "collect" | "photos">): MapMode => (v.collect ? "collect" : v.photos ? "photos" : "map");

/** What Collect lays over the diagram (survey-collect.ts figures it). */
export interface CollectDecor {
  status: (key: string) => "none" | "started" | "missing" | "done" | null;
  /** The wire tag on the line into a node, or null for none. */
  tag: (key: string) => { text: string; state: "none" | "started" | "missing" | "done"; optional: boolean } | null;
  /** The folded pill's words under a node: "12 left". */
  left: (key: string) => number;
  /** Rows left on a housing, for the node's small line. */
  rowsLeft: (key: string) => number;
  totals: { nodes: number; wires: number; photos: number };
  panel: string;
  /** The next node on the work list that is not done, after the one picked. Null when all are done. */
  next?: { key: string; label: string } | null;
}

function tintOf(v: MapView, n: MapNode): string {
  if (n.kind === "source") return "#fff8d6";
  if (n.kind === "load") return "#ffffff";
  return roomTint(v.reg.rooms(), roomOfNode(v.tree, n.key));
}

const kindName: Record<MapNode["kind"], string> = { source: "Source", room: "Room", asset: "Asset", point: "Position", load: "Load" };

// ---- Trail and strip ----

/** One header row (Robert 2026-10-19): back to the site, the mode switch, what is left, the signal,
 *  then Field Find, Scan and Aa. Replaces the trail and the strip. */
// Online, a save waits a second and sends: no count, so the header never flickers under a tap (Robert 2026-10-30).
export function mapBar(v: Pick<MapView, "siteName" | "siteNumber" | "gaps" | "online" | "waiting" | "collect" | "photos" | "photosOk" | "collectView">): string {
  const g = v.gaps;
  const c = v.collect;
  const mode = modeOf(v);
  // Collect opens on the room cards; the diagram stays one tap away (SURVEY_CARDS_C1_WORDS.md item 1).
  // Collect's three views: the cards, the Drawing, the One-Line drawn on it (Part 3).
  const view = c ? `<span class="sv-seg mp-cview"><button type="button" class="${v.collectView === "cards" ? "on" : ""}" data-cd="view-cards">Cards</button><button type="button" class="${v.collectView === "diagram" ? "on" : ""}" data-cd="view-diagram">Drawing</button><button type="button" class="${v.collectView === "oneline" ? "on" : ""}" data-cd="view-oneline">One-Line</button></span>` : "";
  const lead = c
    ? `${view}<button class="mp-wl" type="button" data-mp="worklist">Work List · <b>${c.totals.nodes}</b> left</button>`
    : v.photos
      ? `<span><b>${v.photos.total}</b> photos</span>`
      : `<span><b>${g.onTree} of ${g.of}</b> on the map</span>${g.total ? `<button class="mp-gap" type="button" data-mp="gaps">${g.total} not mapped</button>` : '<span class="mp-ok">All mapped</span>'}`;
  return `<div class="mp-bar">
    <a class="mp-back" href="#/survey/${esc(v.siteNumber)}/walk">‹ ${esc(v.siteName)}</a>
    <span class="sv-seg mp-mode"><button type="button" class="${mode === "map" ? "on" : ""}" data-mp="mode-map">Map</button><button type="button" class="${mode === "collect" ? "on" : ""}" data-mp="mode-collect">Collect</button>${v.photosOk ? `<button type="button" class="${mode === "photos" ? "on" : ""}" data-mp="mode-photos">Photos</button>` : ""}</span>
    <span class="mp-lead">${lead}</span>
    <span class="sv-net" data-sv="outbox"><span class="sv-dot ${v.online ? "on" : ""}"></span>${v.online ? "Online" : "Offline"}${v.waiting && !v.online ? ` · <span class="mp-wait">${v.waiting} waiting</span>` : ""}</span>
    <span class="mp-grow"></span>
    <span class="mp-tools"><button class="mp-line" type="button" data-mp="field-find">Field Find</button><button class="sv-scanbtn" type="button" data-sv="scan">▣ Scan</button><button class="sv-aa" type="button" data-sv="aa">Aa</button></span></div>`;
}

/** One amber line under the header. The most important warning, then "and N more". Other device reads red. */
export function mapWarn(v: Pick<MapView, "siteNumber" | "warn" | "packBusy" | "online">): string {
  const w = v.warn;
  if (!w) return "";
  const first =
    w.first.kind === "outbox"
      ? `<a href="#/survey/${esc(v.siteNumber)}/outbox">${esc(w.first.text)}</a>`
      : `<span${w.first.kind === "other" ? ' class="mp-warn-red"' : ""}>${esc(w.first.text)}</span>`;
  const more = w.more ? `<a class="mp-warn-more" href="#/survey/${esc(v.siteNumber)}/walk">and ${w.more} more</a>` : "";
  const refresh = w.refresh
    ? `<button type="button" data-sv="pack"${!v.online || v.packBusy ? " disabled" : ""}>${v.packBusy ? "Downloading…" : "Refresh Site Pack"}</button>`
    : "";
  return `<div class="mp-warn"><span class="mp-warn-ic">⚠</span>${first}${more}${refresh}</div>`;
}

// ---- Chain panel ----

export function chainPanel(v: MapView, question: string): string {
  if (!v.cur || !v.tree.nodes.get(v.cur)) {
    return `<div class="mp-chain"><div class="mp-endnote">No chain selected. Tap <b>New Chain</b> to start at a source, or tap any node on the diagram to branch from it.</div></div><div class="mp-detail"></div>`;
  }
  const path = pathTo(v.tree, v.cur);
  let h = "";
  path.forEach((n, i) => {
    if (i > 0) {
      const saved = Boolean(n.linkId);
      const dashed = saved && n.how !== "Seen";
      // Insert on every saved line into an asset or a load, a room line included (Robert, iPad test 2026-10-15).
      const canInsert = saved && (n.kind === "asset" || n.kind === "load");
      h += `<div class="mp-link"><span class="mp-ln${dashed ? " dash" : ""}"></span>${saved ? `<button class="mp-how ${n.how}" type="button" data-mp="how" data-k="${esc(n.key)}">${n.how}</button>` : ""}${canInsert ? `<button class="mp-ins" type="button" data-mp="insert" data-k="${esc(n.key)}">Insert</button>` : ""}</div>`;
    }
    h += `<button class="mp-node${n.key === v.cur ? " cur" : ""}" type="button" style="background:${tintOf(v, n)}" data-mp="select" data-k="${esc(n.key)}"><span class="mp-k">${kindName[n.kind]}</span><span class="mp-m"><b>${esc(nodeLabel(v.reg, n, v.links))}</b><small>${esc(nodeSub(v.reg, n, v.links))}</small></span></button>`;
  });
  const c = v.tree.nodes.get(v.cur)!;
  if (c.kind === "load") h += '<div class="mp-endnote">End of chain. Tap any node on the diagram to branch from it.</div>';
  else h += `<div class="mp-link"><span class="mp-ln faint"></span></div><button class="mp-next" type="button" data-mp="next">+ Next: ${esc(question)}</button>`;
  const ks = v.tree.kids.get(v.cur) ?? [];
  if (ks.length) {
    h += `<div class="mp-already">Already below ${esc(nodeLabel(v.reg, c, v.links))}:<br>${ks
      .map((k) => `<button type="button" data-mp="select" data-k="${esc(k)}">${esc(nodeLabel(v.reg, v.tree.nodes.get(k)!, v.links))}</button>`)
      .join("")}</div>`;
  }
  return `<div class="mp-chain">${h}</div><div class="mp-detail">${detail(v, c, ks.length)}</div>`;
}

function detail(v: MapView, c: MapNode, below: number): string {
  const btns: string[] = [];
  if (c.kind === "asset" && isTransfer(v.reg, c.ref)) {
    const second = v.tree.sides.find((s) => s.kind === "second" && s.to === c.key);
    btns.push(second ? `<span class="mp-second">Second source: ${esc(nodeLabel(v.reg, v.tree.nodes.get(second.from)!, v.links))}</span>` : '<button class="sv-btn gold sm" type="button" data-mp="second">Add Second Source</button>');
  }
  if (c.kind === "point" && v.reg.positionsOf(c.asset).includes(c.ref)) {
    const sep = v.tree.sides.find((s) => s.kind === "separate" && s.to === c.key);
    btns.push(sep ? `<span class="mp-second">Fed separately from ${esc(nodeLabel(v.reg, v.tree.nodes.get(sep.from)!, v.links))}</span>` : '<button class="sv-btn line sm" type="button" data-mp="separate">Fed Separately</button>');
  }
  if (!below && (c.draft || c.linkId)) btns.push('<button class="sv-btn line sm" type="button" data-mp="remove">Remove</button>');
  else if (below && c.kind === "asset" && c.linkId && v.reg.wayOut(c.ref) === "out") btns.push('<button class="sv-btn line sm" type="button" data-mp="remove">Remove</button><span class="sv-sub">What is below moves up.</span>');
  else if (below && c.kind === "asset" && c.linkId) btns.push('<span class="sv-sub">To remove it, clear what is below first.</span>');
  // One asset page (ONE_ASSET_PAGE_WORDS.md): the asset's card opens it.
  if (c.kind === "asset") btns.push(`<a class="sv-btn line sm" href="${assetHref(v.siteNumber, fullId(v.siteNumber, c))}">Open Asset Page</a>`);
  return `<div class="mp-fid mono">${esc(fullId(v.siteNumber, c))}</div><div>${esc(nodeSub(v.reg, c, v.links))}</div>${btns.length ? `<div class="sv-btns">${btns.join("")}</div>` : ""}`;
}

// ---- The path (replaces Recent, Robert 2026-10-19) ----

/** Where you are: the chain from the source down to the pick, each step a tap. Collect and Photos. */
export function pathRow(v: MapView): string {
  const steps = v.cur && v.tree.nodes.has(v.cur) ? pathTo(v.tree, v.cur) : [];
  const next = v.collect?.next;
  const nextBtn = v.collect ? (next ? `<button type="button" class="mp-next1" data-cl="jump" data-cl-k="${esc(next.key)}">Next Not Done<small>${esc(next.label)}</small></button>` : '<span class="mp-alldone">All done</span>') : "";
  if (!steps.length) return `<div class="mp-path"><span class="mp-steps muted">Tap a node on the diagram.</span>${nextBtn}</div>`;
  const parts = steps.map((n, i) => {
    const last = i === steps.length - 1;
    // A position sits with its housing: SWBD-01 · A1.
    const sep = i === 0 ? "" : n.kind === "point" ? '<i class="dot">·</i>' : '<i class="sep">›</i>';
    return `${sep}<button type="button" class="${last ? "on" : ""}${n.kind === "room" ? " room" : ""}" data-mp="select" data-k="${esc(n.key)}">${esc(nodeLabel(v.reg, n, v.links))}</button>`;
  });
  return `<div class="mp-path"><span class="mp-steps">${parts.join("")}</span>${nextBtn}</div>`;
}

// ---- Node diagram ----

const W = 128;
const H = 50;
const GX_MAP = 16;
/** Collect widens the gap so two wire tags side by side never touch. */
const GX_COLLECT = 34;
const GY_MAP = 46;
/** Collect adds room between nodes so the wire tag never touches the Fold button (words section 5). */
const GY_COLLECT = 70;

interface VisNode {
  key: string;
  more?: { parent: string; count: number; folded: boolean };
}

export interface Diagram {
  svg: string;
  /** Where the selected node sits, to scroll it into view. */
  focusAt: { x: number; y: number } | null;
}

export function diagram(v: MapView): Diagram {
  const t = v.tree;
  const onPath = new Set<string>();
  const inSub = new Set<string>();
  if (v.cur && t.nodes.has(v.cur)) {
    for (const n of pathTo(t, v.cur)) onPath.add(n.key);
    const mark = (k: string) => {
      inSub.add(k);
      for (const c of t.kids.get(k) ?? []) mark(c);
    };
    mark(v.cur);
  }
  // Gear View step 4: a housing with a door is one node. Gear fed from its positions hangs straight
  // under it, the position named on the line. Loads stay behind the door unless Loads On.
  const housing = (k: string) => {
    const n = t.nodes.get(k);
    return Boolean(n && n.kind === "asset" && v.gear?.(n.ref));
  };
  const via = new Map<string, string>();
  const shown = (key: string): string[] => {
    const ks = t.kids.get(key) ?? [];
    if (!housing(key)) return ks;
    const out: string[] = [];
    for (const k of ks) {
      const n = t.nodes.get(k)!;
      if (n.kind !== "point") {
        out.push(k);
        continue;
      }
      for (const c of t.kids.get(k) ?? []) {
        if (t.nodes.get(c)?.kind === "load") continue;
        via.set(c, n.ref);
        out.push(c);
      }
    }
    return out;
  };
  const loadsOf = (key: string): { key: string; point: string }[] => {
    if (!housing(key)) return [];
    const out: { key: string; point: string }[] = [];
    for (const k of t.kids.get(key) ?? []) {
      const n = t.nodes.get(k)!;
      if (n.kind !== "point") continue;
      for (const c of t.kids.get(k) ?? []) if (t.nodes.get(c)?.kind === "load") out.push({ key: c, point: n.ref });
    }
    return out;
  };
  /** Where a hidden position or load shows: on its housing. */
  const shownKey = (k: string | null): string | null => {
    if (!k) return null;
    let n = t.nodes.get(k);
    if (n?.kind === "load" && n.parent) n = t.nodes.get(n.parent);
    if (n?.kind === "point" && n.parent && housing(n.parent)) return n.parent;
    return k;
  };
  const curShown = shownKey(v.cur);
  // With Focus on, only the loads of the gear you are on; Whole Site, all of them.
  const loadsShown = (key: string) => Boolean(v.loadsOn) && (!v.focus || curShown === key || inSub.has(key) || (v.cur ? shownKey(v.cur) === key : false));
  const countShown = (key: string): number => shown(key).reduce((s, k) => s + 1 + countShown(k), 0);
  const visKids = (key: string): VisNode[] => {
    const ks = shown(key);
    if (!ks.length) return [];
    if (v.fold.has(key)) return [{ key: `more:${key}`, more: { parent: key, count: countShown(key), folded: true } }];
    if (!v.focus || inSub.has(key)) return ks.map((k) => ({ key: k }));
    if (onPath.has(key)) {
      const on = ks.filter((k) => onPath.has(k)).map((k) => ({ key: k }) as VisNode);
      const other = ks.filter((k) => !onPath.has(k));
      if (other.length) on.push({ key: `more:${key}`, more: { parent: key, count: other.reduce((s, k) => s + 1 + countShown(k), 0), folded: false } });
      return on;
    }
    return [{ key: `more:${key}`, more: { parent: key, count: countShown(key), folded: false } }];
  };

  const vis: { node: VisNode; parent: string | null }[] = [];
  const pos = new Map<string, { x: number; y: number }>();
  /** Loads On: the column beside a housing, in slot units, and its loads. */
  const cols = new Map<string, { x: number; y: number; items: { key: string; point: string }[] }>();
  let x = 0;
  const walk = (n: VisNode, parent: string | null, d: number): number => {
    vis.push({ node: n, parent });
    const ks = n.more ? [] : visKids(n.key);
    const loads = !n.more && loadsShown(n.key) ? loadsOf(n.key) : [];
    let px: number;
    if (!ks.length) {
      px = x;
      x += 1;
    } else {
      const xs = ks.map((c) => walk(c, n.key, d + 1));
      px = (xs[0]! + xs[xs.length - 1]!) / 2;
    }
    pos.set(n.key, { x: px, y: d });
    if (loads.length) {
      // The column stands to the right of everything under the housing. Taller, never wider than one slot.
      cols.set(n.key, { x, y: d, items: loads });
      x += 1.45;
    }
    return px;
  };
  for (const r of t.roots) {
    walk({ key: r }, null, 0);
    x += 0.4;
  }
  const GY = v.collect ? GY_COLLECT : GY_MAP;
  const GX = v.collect ? GX_COLLECT : GX_MAP;
  let maxY = 0;
  for (const p of pos.values()) maxY = Math.max(maxY, p.y);
  const CH = 26;
  const CG = 6;
  for (const c of cols.values()) maxY = Math.max(maxY, c.y + Math.ceil((H + 10 + c.items.length * (CH + CG)) / (H + GY)));
  const sw = Math.max(600, x * (W + GX) + 60);
  const sh = (maxY + 1) * (H + GY) + 40;
  const cx = (k: string) => 20 + pos.get(k)!.x * (W + GX) + W / 2;
  const cy = (k: string) => 20 + pos.get(k)!.y * (H + GY);

  if (!t.roots.length) {
    return { svg: '<div class="mp-empty">Nothing mapped yet. Tap New Chain and start at a source.</div>', focusAt: null };
  }

  // A position is a compact chip inside its slot: it is a place on a housing, not equipment.
  const inset = (k: string) => (t.nodes.get(k)?.kind === "point" ? CHIP_INSET : 0);
  let s = `<svg width="${Math.round(sw * v.scale)}" height="${Math.round(sh * v.scale)}" viewBox="0 0 ${sw} ${sh}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Node diagram"><defs><filter id="mp-sh" x="-10%" y="-10%" width="120%" height="140%"><feDropShadow dx="0" dy="1" stdDeviation="1.1" flood-color="#1d2025" flood-opacity=".16"/></filter></defs>`;
  // Lines.
  for (const { node, parent } of vis) {
    if (!parent || !pos.has(parent)) continue;
    const real = node.more ? null : t.nodes.get(node.key)!;
    const how = real?.linkId ? real.how : "Seen";
    const x1 = cx(parent);
    const y1 = cy(parent) + H - inset(parent);
    const x2 = cx(node.key);
    const y2 = cy(node.key) + (node.more ? 8 : inset(node.key));
    const my = cy(parent) + H + GY / 2;
    const hot = onPath.has(node.key) && onPath.has(parent);
    s += `<path d="${elbow(x1, y1, my, x2, y2)}" fill="none" stroke-linejoin="round" stroke="${hot ? "#c99d00" : node.more ? "#b6bac0" : "#111"}" stroke-width="${hot ? 3.5 : 2}"${how === "Seen" ? "" : ' stroke-dasharray="6 5"'}/>`;
    if (how !== "Seen") s += `<text x="${x2 + 6}" y="${y2 - 6}" font-size="10" font-weight="700" fill="${how === "Told" ? "#3d74e0" : "#856404"}">${how}</text>`;
    // The position it leaves from, on the line (DS-04 off C4). Tap it to open the door at that position.
    const at = via.get(node.key);
    if (at) {
      const pw = at.length * 8 + 14;
      const pk = `p:${parent.slice(2)}:${at}`;
      s += `<g class="mp-g" data-mp="select" data-k="${esc(pk)}"><title>Position ${esc(at)}</title><rect x="${x2 + 14}" y="${my - 9}" width="${pw}" height="18" rx="9" fill="#fff" stroke="#9aa1aa"/><text x="${x2 + 21}" y="${my + 4}" font-size="11" font-weight="800" font-family="ui-monospace,Menlo,Consolas,monospace" fill="#111">${esc(at)}</text></g>`;
    }
  }
  // Side lines: second source in orange, a bucket fed separately in black.
  for (const side of t.sides) {
    if (!pos.has(side.from) || !pos.has(side.to)) continue;
    const x1 = cx(side.from);
    const y1 = cy(side.from) + H;
    const x2 = cx(side.to) + W / 2;
    const y2 = cy(side.to) + H / 2;
    const color = side.kind === "second" ? "#d9701e" : "#111";
    s += `<path d="M${x1} ${y1} C${x1} ${y1 + 80} ${x2 + 120} ${y2} ${x2} ${y2}" fill="none" stroke="${color}" stroke-width="2.5"${side.how === "Seen" ? "" : ' stroke-dasharray="6 5"'}/>`;
  }
  // Nodes.
  for (const { node } of vis) {
    const nx = cx(node.key) - W / 2;
    const ny = cy(node.key);
    if (node.more) {
      const m = node.more;
      s += `<g class="mp-g" data-mp="${m.folded ? "fold" : "select"}" data-k="${esc(m.parent)}"><title>Show what is folded under ${esc(nodeLabel(v.reg, t.nodes.get(m.parent)!, v.links))}</title><rect x="${nx + 14}" y="${ny + 8}" width="${W - 28}" height="${H - 16}" rx="17" fill="#fff" stroke="#b6bac0" stroke-width="1.5" stroke-dasharray="4 3"/><text x="${nx + W / 2}" y="${ny + H / 2 + 5}" text-anchor="middle" font-size="13" font-weight="700" fill="#5f646b">${v.collect ? (v.collect.left(m.parent) ? `${v.collect.left(m.parent)} left` : "All done") : `+${m.count} more`}</text></g>`;
      continue;
    }
    const n = t.nodes.get(node.key)!;
    const rx = n.kind === "room" ? 5 : n.kind === "load" ? 22 : n.kind === "point" ? 17 : 8;
    const isCur = n.key === curShown;
    // A position chip: one line, inset in its slot.
    if (n.kind === "point") {
      const iy = ny + CHIP_INSET;
      const ih = H - CHIP_INSET * 2;
      const ix = nx + 14;
      const iw = W - 28;
      const ring = v.collect?.status(n.key) ?? null;
      s += `<g class="mp-g" data-mp="select" data-k="${esc(n.key)}"><title>${esc(fullId(v.siteNumber, n))}</title>`;
      if (isCur) s += `<rect x="${ix - 5}" y="${iy - 5}" width="${iw + 10}" height="${ih + 10}" rx="${rx + 5}" fill="#f5c400"/>`;
      s += `<rect x="${ix}" y="${iy}" width="${iw}" height="${ih}" rx="${rx}" fill="${tintOf(v, n)}" stroke="#1d2025" stroke-width="1.25" filter="url(#mp-sh)"/>`;
      if (ring) s += statusDot(ix + 9, iy + 9, ring);
      s += `<text x="${nx + W / 2}" y="${iy + ih / 2 + 5}" text-anchor="middle" font-size="14" font-weight="800" font-family="ui-monospace,Menlo,Consolas,monospace" fill="#111">${esc(nodeLabel(v.reg, n, v.links).slice(0, 10))}</text>`;
      const pc = v.photos ? v.photos.count(n.key) : null;
      if (pc) s += photoBadge(ix + iw + 4, iy - 2, pc);
      s += "</g>";
      const tagP = v.collect?.tag(n.key) ?? null;
      if (tagP) s += wireTagSvg(v, n, nx, ny, tagP);
      s += foldButton(v, n, nx, ny, shown(n.key).length);
      continue;
    }
    s += `<g class="mp-g" data-mp="select" data-k="${esc(n.key)}"><title>${esc(fullId(v.siteNumber, n))}</title>`;
    const ring = v.collect?.status(n.key) ?? null;
    // Gold is only where you are. Collect status is a corner dot (Robert 2026-10-19).
    if (isCur) s += `<rect x="${nx - 5}" y="${ny - 5}" width="${W + 10}" height="${H + 10}" rx="${rx + 5}" fill="#f5c400"/>`;
    s += `<rect x="${nx}" y="${ny}" width="${W}" height="${H}" rx="${rx}" fill="${tintOf(v, n)}" stroke="#1d2025" stroke-width="${n.kind === "room" ? 2 : 1.25}"${n.floor ? ' stroke-dasharray="4 3"' : ""} filter="url(#mp-sh)"/>`;
    if (ring && n.kind !== "room") s += statusDot(nx + 10, ny + 10, ring);
    if (n.kind === "room") s += `<path d="M${nx + 1} ${ny + 5} Q${nx + 1} ${ny + 1} ${nx + 5} ${ny + 1} H${nx + W - 5} Q${nx + W - 1} ${ny + 1} ${nx + W - 1} ${ny + 5} V${ny + 6} H${nx + 1} Z" fill="#1d2025"/>`;
    let lb = nodeLabel(v.reg, n, v.links);
    if (lb.length > 14) lb = `${lb.slice(0, 13)}…`;
    s += `<text x="${nx + W / 2}" y="${ny + (n.kind === "room" ? 29 : 24)}" text-anchor="middle" font-size="${n.kind === "room" ? 17 : 15}" font-weight="800" font-family="ui-monospace,Menlo,Consolas,monospace" fill="#111">${esc(lb)}</text>`;
    let sm = smallLine(v, n);
    const gear = n.kind === "asset" ? (v.gear?.(n.ref) ?? null) : null;
    if (gear) sm = `${gear.count} ${gear.word} · tap to open`;
    const rl = v.collect && n.kind === "asset" ? v.collect.rowsLeft(n.key) : 0;
    // What is left behind the door, in the gear's own word (Robert 2026-10-19): buckets on an MCC, breakers on a panel.
    if (rl) sm = `${rl} ${(gear ? gear.word.replace(/s$/, "") : "row") + (rl === 1 ? "" : "s")} left`;
    if (sm.length > 24) sm = `${sm.slice(0, 23)}…`;
    if (sm) s += `<text x="${nx + W / 2}" y="${ny + 40}" text-anchor="middle" font-size="${gear ? 10.5 : 11}" fill="#5f646b">${esc(sm)}</text>`;
    // A small door mark: this node opens on its gear front.
    if (gear) for (let c = 0; c < 3; c++) for (let r = 0; r < 3; r++) s += `<rect x="${nx + W - 22 + c * 5.5}" y="${ny + 6 + r * 5}" width="4.2" height="3.8" rx="0.8" fill="#4a5058"/>`;
    const pc = v.photos ? v.photos.count(n.key) : null;
    if (pc !== null) s += photoBadge(nx + W, ny, pc);
    s += "</g>";
    const tag = v.collect?.tag(n.key) ?? null;
    if (tag) s += wireTagSvg(v, n, nx, ny, tag);
    s += foldButton(v, n, nx, ny, shown(n.key).length);
  }
  // Loads On: a stacked column beside each housing, one chip per load: "B2 · 5 HP Bucket Elevator 2".
  for (const [hk, c] of cols) {
    if (!pos.has(hk)) continue;
    const left = 20 + c.x * (W + GX);
    const spine = left + 8;
    const top = cy(hk) + H + 10;
    const last = top + (c.items.length - 1) * (CH + CG) + CH / 2;
    s += `<path d="M${cx(hk) + W / 2} ${cy(hk) + H / 2} H${spine} V${last}" fill="none" stroke="#1d2025" stroke-width="1.5"/>`;
    c.items.forEach((it, i) => {
      const y = top + i * (CH + CG);
      const ln = t.nodes.get(it.key)!;
      const l = v.links.find((k) => k.id === ln.ref);
      const what = [l?.endLoadHp ? `${l.endLoadHp} HP` : "", l?.notes ?? ""].filter(Boolean).join(" ") || nodeLabel(v.reg, ln, v.links);
      let words = what;
      if (words.length > 22) words = `${words.slice(0, 21)}…`;
      const cw = (W + GX) * 1.45 - 24;
      const on = it.key === v.cur;
      s += `<line x1="${spine}" y1="${y + CH / 2}" x2="${spine + 12}" y2="${y + CH / 2}" stroke="#1d2025" stroke-width="1.5"/>`;
      s += `<g class="mp-g" data-mp="select" data-k="${esc(it.key)}"><title>${esc(`${it.point} · ${what}`)}</title>${on ? `<rect x="${spine + 9}" y="${y - 3}" width="${cw + 6}" height="${CH + 6}" rx="16" fill="#f5c400"/>` : ""}<rect x="${spine + 12}" y="${y}" width="${cw}" height="${CH}" rx="13" fill="#fff" stroke="#1d2025" stroke-width="1.25"/><text x="${spine + 22}" y="${y + 17}" font-size="11.5" font-weight="800" font-family="ui-monospace,Menlo,Consolas,monospace" fill="#111">${esc(it.point)}</text><text x="${spine + 30 + it.point.length * 7.5}" y="${y + 17}" font-size="11" fill="#3d434b">${esc(words)}</text></g>`;
    });
  }
  s += "</svg>";
  const fk = curShown && pos.has(curShown) ? curShown : null;
  const focusAt = fk ? { x: cx(fk) * v.scale, y: cy(fk) * v.scale } : null;
  return { svg: s, focusAt };
}

const CHIP_INSET = 8;

/** Collect status on the diagram: gray Not Started, yellow Started, green Done, as on the gear door.
 *  Red means overduty only (Robert 2026-10-20). Yellow takes a dark rim so it never reads as gold. */
const STATUS_FILL: Record<string, string> = { none: "#9aa1aa", started: "#e0a800", missing: "#2f5fc4", done: "#2ea84f" };
const STARTED_RIM = "#4a3a00";
function statusDot(x: number, y: number, st: string): string {
  const c = STATUS_FILL[st] ?? STATUS_FILL.none;
  const rim = st === "started" ? `stroke="${STARTED_RIM}" stroke-width="1.5"` : 'stroke="#fff" stroke-width="1.5"';
  return `<circle cx="${x}" cy="${y}" r="5" fill="${c}" ${rim}/>`;
}

/** Chain panel size steps: 70% to 140%. */
export const CHAIN_MIN = 0.7;
export const CHAIN_MAX = 1.4;
export function chainStep(scale: number, dir: 1 | -1): number {
  return Math.round(Math.max(CHAIN_MIN, Math.min(CHAIN_MAX, scale + dir * 0.1)) * 10) / 10;
}

/** An elbow with soft corners: down, across, down. */
function elbow(x1: number, y1: number, my: number, x2: number, y2: number): string {
  if (Math.abs(x1 - x2) < 0.5) return `M${x1} ${y1} V${y2}`;
  const r = Math.min(8, Math.abs(x2 - x1) / 2, (my - y1) / 2, (y2 - my) / 2);
  const dir = x2 > x1 ? 1 : -1;
  return `M${x1} ${y1} V${my - r} Q${x1} ${my} ${x1 + dir * r} ${my} H${x2 - dir * r} Q${x2} ${my} ${x2} ${my + r} V${y2}`;
}

/** The wire tag, just above the node, on the line into it (Collect words section 5). */
function wireTagSvg(v: MapView, n: MapNode, nx: number, ny: number, tag: { text: string; state: "none" | "started" | "missing" | "done"; optional: boolean }): string {
  const tw = Math.min(W + GX_COLLECT - 6, Math.max(54, tag.text.length * 6.2 + 16));
  const ty = ny - 24 + (n.kind === "point" ? CHIP_INSET : 0);
  // Amber: fill this in. Green: done. Gray: optional.
  const fill = tag.state === "done" ? "#e7f6ec" : tag.optional ? "#f3f4f6" : "#fff3cd";
  const stroke = tag.state === "done" ? "#2ea84f" : tag.optional ? "#c4c7cc" : "#d9b44a";
  const col = tag.state === "done" ? "#1f7a3a" : tag.optional ? "#8a8f96" : "#856404";
  return `<g class="mp-g" data-mp="wire" data-k="${esc(n.key)}"><title>Wire into ${esc(nodeLabel(v.reg, n, v.links))}</title><rect x="${nx + W / 2 - tw / 2}" y="${ty}" width="${tw}" height="20" rx="10" fill="${fill}" stroke="${stroke}" stroke-width="1.25"/><text x="${nx + W / 2}" y="${ty + 14}" text-anchor="middle" font-size="10.5" font-weight="800" fill="${col}">${esc(tag.text)}</text></g>`;
}

/** Fold: a small black dot on the branch point, under the node, where the lines split (Robert 2026-10-16).
 *  It sits on the line the branch opens along, so it never lands on a neighbor at any zoom.
 *  Open: the chevron points up (tap folds the branch up). Folded: it points down (tap opens it down).
 *  The dot is small for the pencil; an unseen ring around it takes a finger. Only where the chain branches. */
function foldButton(v: MapView, n: MapNode, nx: number, ny: number, nk: number): string {
  if (nk < 2 && !v.fold.has(n.key)) return "";
  const folded = v.fold.has(n.key);
  const fx = nx + W / 2;
  const fy = ny + H + (v.collect ? GY_COLLECT : GY_MAP) / 2;
  const chev = folded ? `M${fx - 3.2} ${fy - 1.4} L${fx} ${fy + 1.8} L${fx + 3.2} ${fy - 1.4}` : `M${fx - 3.2} ${fy + 1.6} L${fx} ${fy - 1.6} L${fx + 3.2} ${fy + 1.6}`;
  return `<g class="mp-g mp-fold" data-mp="fold" data-k="${esc(n.key)}"><title>${folded ? "Open" : "Fold"} this branch</title><circle cx="${fx}" cy="${fy}" r="15" fill="#000" fill-opacity="0" pointer-events="all"/><circle cx="${fx}" cy="${fy}" r="7.5" fill="#1d2025" stroke="#fff" stroke-width="1.5"/><path d="${chev}" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></g>`;
}

/** Photos mode: a camera and the count, on the node's top right corner (Robert 2026-10-16: a plain count). */
export function photoBadge(right: number, top: number, count: number): string {
  const text = String(count);
  const w = 30 + text.length * 7.5;
  const x = right - w + 10;
  const y = top - 11;
  const on = count > 0;
  const c = on ? "#111" : "#8a8f96";
  return `<g class="mp-phb"><rect x="${x}" y="${y}" width="${w}" height="22" rx="11" fill="${on ? "#fff" : "#f3f4f6"}" stroke="${on ? "#111" : "#c4c7cc"}" stroke-width="1.5"/><g transform="translate(${x + 7},${y + 4.5})"><rect x="0" y="2.5" width="14" height="9.5" rx="2" fill="none" stroke="${c}" stroke-width="1.5"/><circle cx="7" cy="7.2" r="2.5" fill="none" stroke="${c}" stroke-width="1.4"/><rect x="4" y="0.3" width="6" height="3" rx="1" fill="${c}"/></g><text x="${x + 24 + (text.length * 7.5) / 2}" y="${y + 15.5}" text-anchor="middle" font-size="12.5" font-weight="800" fill="${c}">${text}</text></g>`;
}

function smallLine(v: MapView, n: MapNode): string {
  if (n.kind === "source") return "Source";
  if (n.kind === "room") return "";
  if (n.kind === "point") return shortName(v.reg.byId.get(n.asset) ?? { assetId: n.asset, location: "" });
  if (n.kind === "load") {
    const l = v.links.find((x) => x.id === n.ref);
    return l?.notes || "Load";
  }
  const a = v.reg.byId.get(n.ref);
  if (!a) return "";
  const p = placeOf(a);
  return p.extended ? `Floor · ${p.room}` : p.room ?? "";
}

export function diagramPanel(v: MapView, d: Diagram): string {
  const rooms = v.reg.rooms();
  return `<div class="mp-righthd"><b>Node diagram</b>
      <span class="sv-seg"><button type="button" data-mp="loads-off" class="${v.loadsOn ? "" : "on"}">Loads Off</button><button type="button" data-mp="loads-on" class="${v.loadsOn ? "on" : ""}">Loads On</button></span>
      <span class="sv-seg"><button type="button" data-mp="focus" class="${v.focus ? "on" : ""}">Focus</button><button type="button" data-mp="whole" class="${v.focus ? "" : "on"}">Whole Site</button></span>
      <span class="mp-zoom"><button type="button" data-mp="zoom-out" aria-label="Zoom out">−</button><button type="button" data-mp="fit" class="mp-fit">Fit</button><button type="button" data-mp="zoom-in" aria-label="Zoom in">+</button></span>
    </div><div class="mp-canvaswrap"><div class="mp-canvas" id="mp-canvas">${d.svg}</div>
      <div class="mp-legend mp-key"><span><i></i>Verified</span><span><i class="d"></i>Not Verified</span><span><i class="x"></i>Second source</span>${v.collect ? '<span><b class="mp-sd off"></b>Not Started</span><span><b class="mp-sd start"></b>Started</span><span><b class="mp-sd miss"></b>Missing Data</span><span><b class="mp-sd go"></b>Done</span>' : ""}${rooms
        .map((r) => `<span><span class="mp-sw" style="background:${roomTint(rooms, r)}"></span>${esc(r)}</span>`)
        .join("")}</div></div>`;
}

export function mapScreen(v: MapView, question: string): { above: string; diagram: Diagram } {
  const d = diagram(v);
  const panel = v.collect?.panel ?? v.photos?.panel;
  const left = panel !== undefined ? `<div class="mp-left cl-left">${pathRow(v)}<div class="cl-panel" id="cl-panel">${panel}</div></div>` : `<div class="mp-left"><div class="mp-lefthd"><span>Chain</span><button class="mp-newchain" type="button" data-mp="new-chain">New Chain</button><button class="mp-newchain" type="button" data-mp="add-asset">Add Asset</button><span class="mp-zoom sm"><button type="button" data-mp="chain-out" aria-label="Smaller chain"${(v.chainScale ?? 1) <= CHAIN_MIN ? " disabled" : ""}>−</button><button type="button" data-mp="chain-in" aria-label="Larger chain"${(v.chainScale ?? 1) >= CHAIN_MAX ? " disabled" : ""}>+</button></span></div><div class="mp-chainzoom" style="zoom:${v.chainScale ?? 1}">${chainPanel(v, question)}</div></div>`;
  const above = `${mapBar(v)}${mapWarn(v)}<div class="mp-split${panel !== undefined ? " cl-split" : ""}">${left}<div class="mp-right">${diagramPanel(v, d)}</div></div>`;
  return { above, diagram: d };
}

// ---- The picker sheet ----

export interface SheetTab {
  id: string;
  name: string;
  body: string;
}

export function sheet(title: string, sub: string, tabs: SheetTab[], tab: string, hint: string, extraFoot = ""): string {
  const cur = tabs.find((t) => t.id === tab) ?? tabs[0];
  return `<div class="mp-sheet" data-mp="sheet-bg"><div class="mp-card">
    <div class="mp-sheethd"><b>${esc(title)}${sub ? `<small>${esc(sub)}</small>` : ""}</b><button class="sv-btn line sm" type="button" data-mp="cancel">Cancel</button></div>
    ${tabs.length > 1 ? `<div class="mp-tabs">${tabs.map((t) => `<button type="button" class="${t.id === cur?.id ? "on" : ""}" data-mp="tab" data-v="${esc(t.id)}">${esc(t.name)}</button>`).join("")}</div>` : ""}
    <div class="mp-opts">${cur?.body ?? ""}</div>
    <div class="mp-sheetft"><span class="mp-hint">${esc(hint)}</span>${extraFoot}<button class="sv-btn line sm" type="button" data-mp="sheet-scan">▣ Scan</button><button class="sv-btn line sm" type="button" data-mp="field-find">Field Find</button></div>
  </div></div>`;
}

export function opt(action: string, value: string, big: string, small: string, used = "", extra = ""): string {
  if (used) return `<button class="mp-opt used" type="button" disabled><b>${esc(big)}</b><small>${esc(used)}</small></button>`;
  return `<button class="mp-opt" type="button" data-mp="${action}" data-v="${esc(value)}"${extra}><b>${esc(big)}</b><small>${esc(small)}</small></button>`;
}

export const emptyOpts = (text: string) => `<div class="mp-emptyopt">${esc(text)}</div>`;

export function assetOpts(list: readonly PackAsset[], placed: ReadonlySet<string>, action: string, emptyText: string): string {
  if (!list.length) return emptyOpts(emptyText);
  return list
    .map((a) => {
      const p = placeOf(a);
      return opt(action, a.assetId, shortName(a), `${a.equipmentType}${p.extended ? " · Floor" : ""}`, placed.has(a.assetId) ? "Already on the tree" : "");
    })
    .join("");
}

export function roomOpts(rooms: readonly string[], action: string, skip: string | null = null): string {
  const list = rooms.filter((r) => r !== skip);
  return list.length ? list.map((r) => opt(action, r, r, roomWords(r))).join("") : emptyOpts("No other rooms at this site.");
}

export function breakerForm(lugs: boolean): string {
  return `<form class="mp-form" data-mp-form="breaker">
    <label>Breaker or circuit no.<input id="mp-brk" autocomplete="off" autocapitalize="characters" inputmode="text" placeholder="14 or CB-14"></label>
    <div class="sv-btns"><button class="sv-btn gold" type="submit">Use This Number</button>${lugs ? '<button class="sv-btn line" type="button" data-mp="lugs">Feed-Through (Off The Lugs)</button>' : ""}</div>
    <div class="sv-sub">Stored on the link, never as an asset.</div></form>`;
}

export function loadForm(): string {
  return `<form class="mp-form mp-loadf" data-mp-form="load">
    <label>Type<span class="sv-seg" id="mp-ldt"><button type="button" class="on" data-mp="ldt" data-v="Motor">Motor</button><button type="button" data-mp="ldt" data-v="Non-motor">Other Load</button></span></label>
    <label>HP<input id="mp-ldhp" inputmode="decimal" autocomplete="off" placeholder="from the nameplate or tag"></label>
    <label>Description as found<input id="mp-ldd" autocomplete="off" placeholder="what the door or tag says"></label>
    <label>Site equipment no. (optional)<input id="mp-lde" autocomplete="off" placeholder="for LOTO later"></label>
    <div class="sv-btns mp-full"><button class="sv-btn gold" type="submit">Add Load</button></div></form>`;
}

export function gapList(g: Gaps): string {
  const rows = g.byRoom
    .map((r) => `<div><b>${esc(r.room)}</b> ${r.assets.length ? r.assets.map((a) => `<span class="mono">${esc(shortName(a))}</span>`).join("") : '<em class="mp-ok">all mapped</em>'}</div>`)
    .join("");
  const noRoom = g.noRoom.length ? `<div><b>No room</b> ${g.noRoom.map((a) => `<span class="mono">${esc(a.assetId)}</span>`).join("")}</div>` : "";
  const orph = g.orphans.length ? `<div><b>Positions with no parent</b> ${g.orphans.map((a) => `<span class="mono">${esc(a.assetId)}</span>`).join("")}<br><small>Make the housing on the Asset ID page (Add Asset). Map cannot place these.</small></div>` : "";
  return `<div class="mp-gaplist">${rows}${noRoom}${orph}</div>`;
}

export function noteSheet(title: string, text: string, foot = ""): string {
  return `<div class="mp-sheet" data-mp="sheet-bg"><div class="mp-card"><div class="mp-sheethd"><b>${esc(title)}</b><button class="sv-btn line sm" type="button" data-mp="cancel">Close</button></div><div class="mp-note">${esc(text)}</div>${foot ? `<div class="mp-sheetft">${foot}</div>` : ""}</div></div>`;
}

/** Field Find (ASSET_ID_PAGE_WORDS.md section 3): a missed asset goes through the Asset ID page, then back here to place it. */
export function fieldFindFoot(room: string | null): string {
  return `<span class="mp-hint">${room ? `Opens with ${esc(room)} filled in.` : "Pick the room on the page."}</span><button class="sv-btn gold sm" type="button" data-mp="add-asset">Add Asset</button>`;
}

/** The 6-second Undo bar (CANCEL_WORDS.md section 3), the same look as the photo delete Undo. */
export function undoToast(text: string): string {
  return text ? `<div class="mp-undo" role="status"><span>${esc(text)}</span><button type="button" data-mp="undo">Undo</button></div>` : "";
}

export function toast(text: string): string {
  return text ? `<div class="mp-toast" role="status">${esc(text)}</div>` : "";
}

